import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Package, 
  AlertTriangle, 
  TrendingDown,
  IndianRupee,
  BarChart3,
  RefreshCw,
  Download,
  Store,
  ArrowRight,
  Clock,
  CheckCircle,
  XCircle,
  FileSpreadsheet,
  Boxes,
  ShoppingCart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StockSenseDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/stock-sense`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load StockSense dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/stock-sense/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="stock-sense-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="stock-sense-no-data">
        <Package className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonStockSense agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>
          Go to Agents
        </Button>
      </div>
    );
  }

  const overview = data?.inventoryHealthDashboard?.overallMetrics || {};
  const storeHotspots = data?.storeHotspots || {};
  const skuHotspots = data?.skuHotspots || {};
  const actionPlan = data?.actionPlan || {};
  const recommendations = data?.recommendations || [];

  // Prepare chart data
  const storeRollup = data?.inventoryHealthDashboard?.storeRollup || [];
  const needStockStores = storeHotspots?.needStock || [];
  const excessStockStores = storeHotspots?.excessStock || [];

  const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6'];

  return (
    <div className="space-y-6" data-testid="stock-sense-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Package className="h-8 w-8 text-blue-600" />
            <TranslatedText text="StockSense Dashboard" cacheKey="stocksense:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Inventory Intelligence & Stock Health Analysis" cacheKey="stocksense:subtitle" />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200">
        <CardHeader>
          <CardTitle><TranslatedText text="Executive Summary" cacheKey="stocksense:execsummary" /></CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="stocksense:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total On-Hand" cacheKey="stocksense:total_onhand" /></p>
                <p className="text-xl font-bold text-blue-900">{formatNumber(overview.totalOnhandQty)}</p>
              </div>
              <Boxes className="h-6 w-6 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Avg Cover Days" cacheKey="stocksense:avg_cover" /></p>
                <p className="text-xl font-bold text-green-900">{overview.averageCoverDays || 'N/A'}</p>
              </div>
              <Clock className="h-6 w-6 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Dead Stock Value" cacheKey="stocksense:dead_stock" /></p>
                <p className="text-xl font-bold text-red-900">{formatCurrency(overview.deadStockValue) || 'N/A'}</p>
              </div>
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Stockout Risk SKUs" cacheKey="stocksense:stockout_risk" /></p>
                <p className="text-xl font-bold text-orange-900">{overview.stockoutRiskSKUs || 0}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Excess Stock SKUs" cacheKey="stocksense:excess_stock" /></p>
                <p className="text-xl font-bold text-yellow-900">{overview.excessStockSKUs || 0}</p>
              </div>
              <TrendingDown className="h-6 w-6 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="90+ Days Aged" cacheKey="stocksense:aged_90" /></p>
                <p className="text-xl font-bold text-purple-900">{overview.pctInventory90PlusDays || '0%'}</p>
              </div>
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DETAILED RECOMMENDATIONS WITH UNDERLYING DATA */}
      <Card className="border-2 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <CheckCircle className="h-5 w-5" />
            <TranslatedText text="Priority Actions with Detailed Analysis" cacheKey="stocksense:priority_actions" />
          </CardTitle>
          <CardDescription style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Each recommendation includes the underlying data - click to expand" cacheKey="stocksense:rec_desc" />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* RECOMMENDATION 1: Stockout Risk SKUs */}
          <div className="border-2 border-red-200 rounded-lg overflow-hidden">
            <div className="bg-red-50 p-4 flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-sm font-bold text-white">1</span>
              <div className="flex-1">
                <h4 className="font-bold text-red-800" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Urgent Replenishment for Critical Stockout SKUs" cacheKey="stocksense:rec1_title" />
                </h4>
                <p className="text-sm text-red-600" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Impact: Prevent revenue loss from lost sales" cacheKey="stocksense:rec1_impact" />
                </p>
                <p className="text-sm mt-1 text-slate-600">
                  {skuHotspots?.stockoutRisk?.length || 0} SKUs at critical stockout risk - estimated lost sales of {formatCurrency((skuHotspots?.stockoutRisk || []).reduce((sum, sku) => {
                    const val = sku.lostSalesValue;
                    return sum + (typeof val === 'string' ? parseFloat(val.replace(/[₹,]/g, '')) || 0 : val || 0);
                  }, 0))}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleExport('stockout_risk', 'Stockout Risk')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All ({skuHotspots?.stockoutRisk?.length || 0})
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-red-800">SKU ID</th>
                    <th className="px-3 py-2 text-left font-semibold text-red-800">Store</th>
                    <th className="px-3 py-2 text-right font-semibold text-red-800">Cover Days</th>
                    <th className="px-3 py-2 text-right font-semibold text-red-800">Daily Velocity</th>
                    <th className="px-3 py-2 text-right font-semibold text-red-800">Lost Sales Qty</th>
                    <th className="px-3 py-2 text-right font-semibold text-red-800">Lost Sales Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-red-100">
                  {(skuHotspots?.stockoutRisk || []).slice(0, 10).map((sku, idx) => (
                    <tr key={idx} className="hover:bg-red-50">
                      <td className="px-3 py-2 font-mono font-medium">{sku.skuId}</td>
                      <td className="px-3 py-2">{sku.storeId}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded ${sku.coverDays < 3 ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'}`}>
                          {sku.coverDays}d
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{sku.dailyVelocity}/day</td>
                      <td className="px-3 py-2 text-right text-red-600 font-medium">{sku.lostSalesQty}</td>
                      <td className="px-3 py-2 text-right text-red-700 font-bold">{sku.lostSalesValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(skuHotspots?.stockoutRisk?.length || 0) > 10 && (
                <div className="p-2 bg-red-50 text-center text-sm text-red-600">
                  + {(skuHotspots?.stockoutRisk?.length || 0) - 10} more SKUs - Export to see all
                </div>
              )}
            </div>
          </div>

          {/* RECOMMENDATION 2: Audit Negative Stock */}
          <div className="border-2 border-orange-200 rounded-lg overflow-hidden">
            <div className="bg-orange-50 p-4 flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-sm font-bold text-white">2</span>
              <div className="flex-1">
                <h4 className="font-bold text-orange-800" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Audit Negative Stock SKUs" cacheKey="stocksense:rec2_title" />
                </h4>
                <p className="text-sm text-orange-600" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Impact: Recover inventory accuracy" cacheKey="stocksense:rec2_impact" />
                </p>
                <p className="text-sm mt-1 text-slate-600">
                  {data?.negativeStockItems?.length || storeRollup.filter(s => s.onhandQty < 0).length || 0} SKUs with negative on-hand quantities require immediate audit
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleExport('negative_stock', 'Negative Stock')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
              </Button>
            </div>
            {(data?.negativeStockItems?.length > 0 || storeRollup.filter(s => s.onhandQty < 0).length > 0) ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-orange-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-orange-800">SKU ID</th>
                      <th className="px-3 py-2 text-left font-semibold text-orange-800">Store</th>
                      <th className="px-3 py-2 text-right font-semibold text-orange-800">Negative Qty</th>
                      <th className="px-3 py-2 text-right font-semibold text-orange-800">Cover Days</th>
                      <th className="px-3 py-2 text-left font-semibold text-orange-800">Issue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-orange-100">
                    {(data?.negativeStockItems || storeRollup.filter(s => s.onhandQty < 0)).slice(0, 5).map((item, idx) => (
                      <tr key={idx} className="hover:bg-orange-50">
                        <td className="px-3 py-2 font-mono font-medium">{item.skuId || item.productCode || '-'}</td>
                        <td className="px-3 py-2">{item.storeName || item.storeId || '-'}</td>
                        <td className="px-3 py-2 text-right text-red-600 font-bold">{item.onhandQty || item.negativeQty}</td>
                        <td className="px-3 py-2 text-right">{item.coverDays || 0}d</td>
                        <td className="px-3 py-2 text-orange-600">Negative inventory - audit required</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(data?.negativeStockItems?.length || 0) > 5 && (
                  <div className="p-2 bg-orange-50 text-center text-sm text-orange-600">
                    + {(data?.negativeStockItems?.length || 0) - 5} more items - Export to see all
                  </div>
                )}
              </div>
            ) : (
              <div className="p-4 text-center text-green-600 bg-green-50">
                <CheckCircle className="h-5 w-5 inline mr-2" />
                No negative stock detected - inventory data is clean
              </div>
            )}
          </div>

          {/* RECOMMENDATION 3: Liquidate Dead Stock */}
          <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
            <div className="bg-purple-50 p-4 flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-sm font-bold text-white">3</span>
              <div className="flex-1">
                <h4 className="font-bold text-purple-800" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Liquidate Dead Stock" cacheKey="stocksense:rec3_title" />
                </h4>
                <p className="text-sm text-purple-600" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Impact: Recover blocked capital" cacheKey="stocksense:rec3_impact" />
                </p>
                <p className="text-sm mt-1 text-slate-600">
                  {overview.deadStockSKUs || 0} SKUs identified as dead stock (aged &gt;120 days with &lt;20% sell-through)
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleExport('liquidation_plan', 'Liquidation Plan')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" />Export All ({data?.liquidationPlan?.length || 0})
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-purple-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-purple-800">SKU ID</th>
                    <th className="px-3 py-2 text-left font-semibold text-purple-800">Store</th>
                    <th className="px-3 py-2 text-right font-semibold text-purple-800">Age (Days)</th>
                    <th className="px-3 py-2 text-right font-semibold text-purple-800">Qty On-Hand</th>
                    <th className="px-3 py-2 text-left font-semibold text-purple-800">Recommended Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-100">
                  {(data?.liquidationPlan || []).slice(0, 10).map((item, idx) => (
                    <tr key={idx} className="hover:bg-purple-50">
                      <td className="px-3 py-2 font-mono font-medium">{item.skuId}</td>
                      <td className="px-3 py-2">{item.storeId}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`px-2 py-0.5 rounded ${item.ageDays > 180 ? 'bg-red-200 text-red-800' : 'bg-purple-200 text-purple-800'}`}>
                          {item.ageDays}d
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{item.onhandQty}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 bg-purple-200 text-purple-800 rounded text-xs">
                          {item.recommendedAction}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data?.liquidationPlan?.length || 0) > 10 && (
                <div className="p-2 bg-purple-50 text-center text-sm text-purple-600">
                  + {(data?.liquidationPlan?.length || 0) - 10} more SKUs - Export to see all
                </div>
              )}
            </div>
          </div>

          {/* RECOMMENDATION 4: Transfer Stock */}
          {(data?.transferPlan?.length || 0) > 0 && (
            <div className="border-2 border-blue-200 rounded-lg overflow-hidden">
              <div className="bg-blue-50 p-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white">4</span>
                <div className="flex-1">
                  <h4 className="font-bold text-blue-800">Inter-Store Transfers Recommended</h4>
                  <p className="text-sm text-blue-600">Impact: Balance inventory across network</p>
                  <p className="text-sm mt-1 text-slate-600">
                    {data?.transferPlan?.length || 0} transfer opportunities identified to optimize stock distribution
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleExport('transfer_plan', 'Transfer Plan')}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-blue-800">SKU ID</th>
                      <th className="px-3 py-2 text-left font-semibold text-blue-800">From Store</th>
                      <th className="px-3 py-2 text-center font-semibold text-blue-800">→</th>
                      <th className="px-3 py-2 text-left font-semibold text-blue-800">To Store</th>
                      <th className="px-3 py-2 text-right font-semibold text-blue-800">Transfer Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    {(data?.transferPlan || []).slice(0, 10).map((transfer, idx) => (
                      <tr key={idx} className="hover:bg-blue-50">
                        <td className="px-3 py-2 font-mono font-medium">{transfer.skuId}</td>
                        <td className="px-3 py-2">{transfer.donorStore}</td>
                        <td className="px-3 py-2 text-center"><ArrowRight className="h-4 w-4 text-blue-500 inline" /></td>
                        <td className="px-3 py-2">{transfer.receiverStore}</td>
                        <td className="px-3 py-2 text-right font-bold text-blue-700">{transfer.transferQty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store Hotspots */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Need Stock Stores */}
        <Card className="border-orange-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="h-5 w-5" />
                Stores Needing Stock ({needStockStores.length})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExport('stockout_risk', 'Stockout Risk SKUs')}
                disabled={exporting === 'stockout_risk'}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                {exporting === 'stockout_risk' ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {needStockStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 rounded border border-orange-100">
                  <div>
                    <p className="font-medium">{store.storeName || store.storeId}</p>
                    <p className="text-xs text-slate-500">Cover: {store.coverDays} days | Risk SKUs: {store.stockoutRiskSKUs}</p>
                  </div>
                  <span className="text-sm font-semibold text-red-600">
                    {formatCurrency(store.estimatedLostSales)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Excess Stock Stores */}
        <Card className="border-yellow-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-yellow-700">
                <TrendingDown className="h-5 w-5" />
                Stores with Excess Stock ({excessStockStores.length})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExport('dead_stock', 'Dead Stock SKUs')}
                disabled={exporting === 'dead_stock'}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                {exporting === 'dead_stock' ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {excessStockStores.slice(0, 10).map((store, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-100">
                  <div>
                    <p className="font-medium">{store.storeName || store.storeId}</p>
                    <p className="text-xs text-slate-500">Cover: {store.coverDays} days | Excess SKUs: {store.excessSKUs}</p>
                  </div>
                  <span className="text-sm font-semibold text-yellow-600">
                    {formatCurrency(store.excessValue)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Action Plan
            </CardTitle>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleExport('action_plan', 'Action Plan')}
              disabled={exporting === 'action_plan'}
            >
              <Download className="h-4 w-4 mr-1" />
              {exporting === 'action_plan' ? 'Exporting...' : 'Download Action Plan'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* P0 - Immediate */}
            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-semibold text-red-600 mb-2">P0 - Immediate ({actionPlan.P0_Immediate?.length || 0})</h4>
              <div className="space-y-2">
                {actionPlan.P0_Immediate?.slice(0, 3).map((action, idx) => (
                  <div key={idx} className="text-sm p-2 bg-red-50 rounded">
                    <p className="font-medium">{action.action}</p>
                    {action.impact && <p className="text-xs text-slate-500">Impact: {action.impact}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* P1 - This Week */}
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-semibold text-orange-600 mb-2">P1 - This Week ({actionPlan.P1_ThisWeek?.length || 0})</h4>
              <div className="space-y-2">
                {actionPlan.P1_ThisWeek?.slice(0, 3).map((action, idx) => (
                  <div key={idx} className="text-sm p-2 bg-orange-50 rounded">
                    <p className="font-medium">{action.action}</p>
                    {action.impact && <p className="text-xs text-slate-500">Impact: {action.impact}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* P2 - Next Cycle */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-blue-600 mb-2">P2 - Next Cycle ({actionPlan.P2_NextCycle?.length || 0})</h4>
              <div className="space-y-2">
                {actionPlan.P2_NextCycle?.slice(0, 3).map((action, idx) => (
                  <div key={idx} className="text-sm p-2 bg-blue-50 rounded">
                    <p className="font-medium">{action.action}</p>
                    {action.impact && <p className="text-xs text-slate-500">Impact: {action.impact}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer & Liquidation Plans */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transfer Plan */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ArrowRight className="h-5 w-5 text-blue-600" />
                Transfer Plan ({data?.transferPlan?.length || 0})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExport('transfer_plan', 'Transfer Plan')}
                disabled={exporting === 'transfer_plan'}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.transferPlan?.slice(0, 10).map((transfer, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                  <span className="font-mono bg-blue-100 px-2 py-1 rounded">{transfer.skuId}</span>
                  <span className="text-slate-500">{transfer.donorStore}</span>
                  <ArrowRight className="h-4 w-4 text-blue-500" />
                  <span className="text-slate-500">{transfer.receiverStore}</span>
                  <span className="ml-auto font-semibold">{transfer.transferQty} units</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Liquidation Plan */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-red-600" />
                Liquidation Plan ({data?.liquidationPlan?.length || 0})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExport('liquidation_plan', 'Liquidation Plan')}
                disabled={exporting === 'liquidation_plan'}
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {data?.liquidationPlan?.slice(0, 10).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                  <div>
                    <span className="font-mono bg-red-100 px-2 py-1 rounded">{item.skuId}</span>
                    <span className="ml-2 text-slate-500">@ {item.storeId}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-red-600 font-medium">{item.ageDays} days old</span>
                    <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">{item.recommendedAction}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export All Data */}
      <Card className="bg-slate-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Export Full Analysis</h3>
              <p className="text-sm text-slate-500">Download complete inventory data for detailed analysis</p>
            </div>
            <Button 
              onClick={() => handleExport('all_inventory', 'All Inventory Data')}
              disabled={exporting === 'all_inventory'}
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting === 'all_inventory' ? 'Exporting...' : 'Export All Inventory'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StockSenseDashboard;
